/**
 * Runtime validation for step metadata fields.
 *
 * Validates that metadata fields match the expected schema, showing notifications
 * in development mode or logging to console in production. Validation errors
 * do not break the application - data is returned unchanged.
 */

import {IProtocolStepKind} from "./stepKind";
import {stepMetadataFields, getMetadataFieldsForKind} from "./stepMetadataSchemas";
import {showErrorNotification} from "../../common/notifications";

/**
 * Context for validation - helps identify where the validation issue occurred.
 */
export type TValidationContext = "stepDTOToStep" | "stepDefinitionDTOToStep" | "extractMetadata" | "extractProtocolStepMetadata";

/**
 * Tracks warned (kind, field) tuples to prevent duplicate warnings per session.
 */
const warnedFields = new Set<string>();

/**
 * Check if we should show validation notifications (dev mode or test server).
 * Shows notifications in:
 * - Development mode (NODE_ENV !== 'production')
 * - Test server (hostname is 'neurotune.test.bios.digital')
 */
const shouldShowNotifications = (): boolean => {
    // Always show notifications on test server
    if (typeof window !== 'undefined' && window.location?.hostname === 'neurotune.test.bios.digital') {
        return true;
    }
    // Show in development mode
    return process.env.NODE_ENV !== 'production';
};

/**
 * Creates a unique key for tracking warned fields.
 */
const createWarnKey = (kind: IProtocolStepKind, context: TValidationContext, fields: string[]): string => {
    return `${kind}:${context}:${fields.sort().join(",")}`;
};

/**
 * Reports validation issues - shows notification in dev mode, logs to console in production.
 */
const reportValidationIssue = (
    kind: IProtocolStepKind,
    context: TValidationContext,
    unexpectedFields: string[],
    missingFields: string[]
): void => {
    const warnKey = createWarnKey(kind, context, [...unexpectedFields, ...missingFields]);

    // Skip if we've already warned about this exact issue
    if (warnedFields.has(warnKey)) {
        return;
    }
    warnedFields.add(warnKey);

    const messages: string[] = [];

    if (unexpectedFields.length > 0) {
        messages.push(`Unexpected fields: ${unexpectedFields.join(", ")}`);
    }

    if (missingFields.length > 0) {
        messages.push(`Missing expected fields: ${missingFields.join(", ")}`);
    }

    const fullMessage = `Step metadata validation failed for ${kind} in ${context}:\n${messages.join("\n")}`;

    if (shouldShowNotifications()) {
        showErrorNotification({
            title: "Step Metadata Validation Error",
            message: fullMessage,
            autoClose: 10000,
        });
    } else {
        console.warn(`[StepMetadataValidation] ${fullMessage}`);
    }
};

/**
 * Validates step metadata fields against the schema.
 *
 * @param kind - The step kind
 * @param metadata - The metadata object to validate
 * @param context - Where the validation is occurring (for error messages)
 * @returns The metadata unchanged (validation is non-blocking)
 */
export const validateStepMetadata = <T extends Record<string, unknown>>(
    kind: IProtocolStepKind,
    metadata: T,
    context: TValidationContext
): T => {
    const allowedFields = stepMetadataFields[kind];

    if (!allowedFields) {
        // Unknown step kind - this should be caught by exhaustiveness tests
        const warnKey = `unknown:${kind}:${context}`;
        if (!warnedFields.has(warnKey)) {
            warnedFields.add(warnKey);
            const message = `Unknown step kind: ${kind}`;
            if (shouldShowNotifications()) {
                showErrorNotification({
                    title: "Unknown Step Kind",
                    message,
                    autoClose: 10000,
                });
            } else {
                console.warn(`[StepMetadataValidation] ${message}`);
            }
        }
        return metadata;
    }

    const metadataKeys = Object.keys(metadata);
    const allowedFieldsWithoutKind = getMetadataFieldsForKind(kind);

    // Find unexpected fields (in metadata but not in schema)
    const unexpectedFields = metadataKeys.filter(
        key => !allowedFieldsWithoutKind.includes(key)
    );

    // For extraction contexts, we don't check for missing fields since metadata can be partial
    // For DTO conversion contexts, we also allow partial since fields are optional
    const missingFields: string[] = [];

    if (unexpectedFields.length > 0 || missingFields.length > 0) {
        reportValidationIssue(kind, context, unexpectedFields, missingFields);
    }

    return metadata;
};

/**
 * Clears the warned fields cache. Useful for testing.
 */
export const clearValidationWarnings = (): void => {
    warnedFields.clear();
};

/**
 * Gets the current count of unique warnings issued. Useful for testing.
 */
export const getWarningCount = (): number => {
    return warnedFields.size;
};

